import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'

export type MiHoja = components['schemas']['MiHojaRead']
export type MiFila = components['schemas']['MiFilaRead']
export type MisRequisitos = components['schemas']['Page_MiFilaRead_']

export const MIS_REQUISITOS_PAGE_SIZE = 20
type UploadTarget = components['schemas']['UploadTargetRead']

function base(businessId: string) {
  return `/businesses/${businessId}/sgsst/mi/cumplimiento`
}

function miKey(businessId: string | undefined) {
  return ['mi-cumplimiento', businessId]
}

function requisitosKey(businessId: string | undefined) {
  return ['mi-cumplimiento-requisitos', businessId]
}

/** Solo la cabecera, sin datos de terceros; los requisitos viajan aparte y paginados (R6). */
export function useMiCumplimiento(businessId: string | undefined) {
  return useQuery({
    queryKey: miKey(businessId),
    queryFn: async () => (await api.get<MiHoja>(base(businessId!))).data,
    enabled: !!businessId,
  })
}

/** Pendientes primero. */
export function useMisRequisitos(businessId: string | undefined, page: number) {
  return useQuery({
    queryKey: [...requisitosKey(businessId), page],
    queryFn: async () =>
      (
        await api.get<MisRequisitos>(`${base(businessId!)}/requisitos`, {
          params: {page, page_size: MIS_REQUISITOS_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

/** El `object_key` ya subido sobrevive a cerrar la pestaña para que el servidor pueda deduplicar el reintento. */
function claveDelIntento(businessId: string, requisitoId: string) {
  return `mi-aporte:${businessId}:${requisitoId}`
}

function intentoGuardado(clave: string): UploadTarget | null {
  try {
    const crudo = localStorage.getItem(clave)
    return crudo ? (JSON.parse(crudo) as UploadTarget) : null
  } catch {
    // Con el almacenamiento bloqueado se pierde la idempotencia del reintento, no el aporte.
    return null
  }
}

function guardarIntento(clave: string, target: UploadTarget | null) {
  try {
    if (target === null) localStorage.removeItem(clave)
    else localStorage.setItem(clave, JSON.stringify(target))
  } catch {
    return
  }
}

export function hayIntentoPendiente(businessId: string, requisitoId: string) {
  return intentoGuardado(claveDelIntento(businessId, requisitoId)) !== null
}

/** El registro es idempotente por `object_key`: el destino se guarda al acuñarse y se reusa hasta registrar, así
 * un reintento no crea una segunda evidencia. */
export function useAportar(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {requisitoId: string; file: File; onProgress?: (pct: number) => void}) => {
      const ruta = `${base(businessId)}/${input.requisitoId}`
      const clave = claveDelIntento(businessId, input.requisitoId)
      const pendiente = intentoGuardado(clave)
      const target =
        pendiente ??
        (
          await api.post<UploadTarget>(`${ruta}/upload-target`, {
            filename: input.file.name,
            content_type: input.file.type,
            size_bytes: input.file.size,
          })
        ).data
      if (pendiente === null) {
        guardarIntento(clave, target)
        await uploadToStorage(target, input.file, input.onProgress ?? (() => {}))
      }
      const hoja = (
        await api.post<MiHoja>(`${ruta}/aportar`, {
          object_key: target.object_key,
          filename: input.file.name,
          content_type: input.file.type,
          size_bytes: input.file.size,
        })
      ).data
      guardarIntento(clave, null)
      return hoja
    },
    onSuccess: (data) => {
      queryClient.setQueryData(miKey(businessId), data)
      // La respuesta es la cabecera; la fila cambiada vive en la lista paginada.
      queryClient.invalidateQueries({queryKey: requisitosKey(businessId)})
    },
  })
}

/** Solo mientras siga sugerido y sin revisar: corregirse antes de la revisión no es un acto legal. */
export function useRetirarAportado(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {requisitoId: string; evidenceId: string}) =>
      (await api.delete<MiHoja>(`${base(businessId)}/${input.requisitoId}/aportado/${input.evidenceId}`)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(miKey(businessId), data)
      queryClient.invalidateQueries({queryKey: requisitosKey(businessId)})
    },
  })
}
